import { useTranslation } from 'react-i18next';
import { Card } from '@/common/components/Card/Card';
import { Grid } from '@/common/components/Grid/Grid';
import { Heading } from '@/common/components/Heading/Heading';
import { LabeledValue } from '@/common/components/LabeledValue/LabeledValue';
import { Stack } from '@/common/components/Stack/Stack';
import { getCountryName } from '@/common/constants/country-list';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import {
  useRequestCard,
  useRequestPlasticCard,
} from '@/common/integrations/menhir/employee/card/card.hooks';
import { FlowLayout } from '@/common/layouts/FlowLayout/FlowLayout';
import { useFlowLayout } from '@/common/layouts/FlowLayout/FlowLayout.context';
import { IAddress } from '@/common/types/general';
import { isErrorObject } from '@/common/utils/error';
import { notify } from '@/common/utils/toast/toast';
import { useLocalSearchParams } from 'expo-router';

type TConfirmDetailsStepProps = {
  cardHolderName: string;
  address?: IAddress;
  digitalCardId?: string;
};

const mapCountry = (countryCode: string) => {
  return getCountryName(countryCode) || countryCode;
};

export const ConfirmDetailsStep = ({
  cardHolderName,
  address,
  digitalCardId,
}: TConfirmDetailsStepProps) => {
  const { t } = useTranslation();
  const { dismiss } = useFlowLayout();

  const searchParams = useLocalSearchParams<{
    origin?: 'onboarding' | 'login';
  }>();
  const { mutateAsync: requestCard, isLoading: requestCardLoading } =
    useRequestCard();

  const {
    mutateAsync: requestPlasticCard,
    isLoading: isRequestPlasticCardLoading,
  } = useRequestPlasticCard();

  const handleSubmit = async () => {
    try {
      AnalyticsService.trackCustomEvent('request_card_button_clicked', {
        context: searchParams.origin ?? 'login',
      });
      if (digitalCardId) {
        await requestPlasticCard({
          cardId: digitalCardId,
          address,
          cardHolderName,
        });
      } else {
        await requestCard({
          address,
          cardHolderName,
        });
      }
      notify({
        type: 'success',
        message: t('dashboard.request_card.form.submit.success'),
      });
      dismiss();
    } catch (error) {
      if (isErrorObject(error) && 'zipcode' in error.errors) {
        notify({
          type: 'error',
          message: t('errors.input.invalid_zipcode'),
        });
      } else {
        notify({
          type: 'error',
          message: t('dashboard.request_card.notification.error'),
        });
      }
    }
  };

  return (
    <FlowLayout.Content
      primaryAction={{
        children: t('dashboard.request_card.steps.three.submit.label'),
        onPress: handleSubmit,
        loading: isRequestPlasticCardLoading || requestCardLoading,
      }}
      secondaryAction={{
        children: t('general.navigation.back'),
        disabled: isRequestPlasticCardLoading || requestCardLoading,
      }}
    >
      <Stack gap="form">
        <Card plain>
          <LabeledValue
            label={t('dashboard.request_card.form.fields.name_on_card.label')}
            value={cardHolderName}
          />
        </Card>
        {address && (
          <Card plain>
            <Stack gap="form">
              <Heading level={4}>
                {t('dashboard.request_card.form.fields.shipping_address.label')}
              </Heading>
              <LabeledValue
                label={t(
                  'dashboard.request_card.form.fields.street_and_number.label'
                )}
                value={address.addressLine1}
              />
              <LabeledValue
                label={t(
                  'dashboard.request_card.form.fields.other_address_details.label'
                )}
                value={address.addressLine2 ? address.addressLine2 : '\u2014'}
              />
              <Grid>
                <LabeledValue
                  label={t('dashboard.request_card.form.fields.zip_code.label')}
                  value={address.zipCode}
                />
                <LabeledValue
                  label={t('dashboard.request_card.form.fields.city.label')}
                  value={address.city}
                />
                {address.district && (
                  <LabeledValue
                    label={t(
                      'dashboard.request_card.form.fields.district.label'
                    )}
                    value={address.district}
                  />
                )}
                <LabeledValue
                  label={t('dashboard.request_card.form.fields.country.label')}
                  value={mapCountry(address.country)}
                />
              </Grid>
            </Stack>
          </Card>
        )}
      </Stack>
    </FlowLayout.Content>
  );
};
